import { createTheme } from "@mui/material/styles";

const theme = createTheme({
  palette: {
    primary: {
      main: "#0F766E", // Custom green color from the design
      light: "#14B8A6",
      dark: "#115E59",
    },
    secondary: {
      main: "#64748B",
      light: "#94A3B8",
      dark: "#475569",
    },
    background: {
      default: "#F8FAFC",
      paper: "#FFFFFF",
    },
  },
  typography: {
    htmlFontSize: 12, // Default is 16px, reducing it scales all rem units
    fontFamily: '"Poppins", "Inter", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: "2rem",
      fontWeight: 600,
    },
    h2: {
      fontSize: "1.5rem",
      fontWeight: 600,
    },
    h3: {
      fontSize: "1.25rem",
      fontWeight: 600,
    },
    body1: {
      fontSize: "1rem",
    },
    body2: {
      fontSize: "0.875rem", // 10.5px (for smaller text like table cells)
    },
    button: {
      textTransform: "none",
      fontSize: "0.875rem", // 10.5px at htmlFontSize: 12 (smaller button text)
      fontWeight: 600,
    },
  },
  spacing: 6, // Default spacing unit reduced from 8 (MUI default) to 4 for tighter layout
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: "6px 12px", // Reduced from 8px 16px for compactness
          fontSize: "0.875rem", // Ensure button text is smaller
        },
        contained: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          textWrap: "wrap !important",
          fontSize: "0.875rem", // 10.5px for table cells
          padding: "4px 8px", // Reduced padding for tighter table rows
          width: "auto !important", // Allow cells to size naturally but prevent dynamic resizing
          minWidth: 0, // Prevent cells from expanding unnecessarily
          maxWidth: "none !important", // Ensure no unexpected width changes
        },
        head: {
          fontSize: "1rem", // 12px for table headers
          fontWeight: 600,
          padding: "6px 8px", // Reduced padding for headers
          width: "auto !important", // Fix header widths
          minWidth: 0, // Prevent headers from expanding unnecessarily
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow:
            "0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)",
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          width: "100%", // Ensure Grids don’t overflow
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          margin: "16px", // Add slight margin for mobile screens
          width: "90%", // Use percentage for responsiveness
          maxWidth: "600px", // Cap the maximum width
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: {
          "& .MuiDialog-paper": {
            margin: "16px",
            width: "90%",
            maxWidth: "600px",
          },
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        root: {
          width: "28 !important", // Reduced from 50px (matches your CustomSwitch, but smaller)
          height: "16 !important", // Reduced from 26px (matches your CustomSwitch, but smaller)
          padding: "0 !important", // Preserve padding: 0 from CustomSwitch
          willChange: "transform", // Optimize for smooth transitions
          transition: "none !important", // Remove default transitions to prevent jitter
        },
        switchBase: {
          padding: "0 !important", // Preserve padding: 0 from CustomSwitch
          margin: "2 !important", // Reduced margin to match smaller size
          willChange: "transform", // Optimize for smooth transitions
          transition: "transform 150ms cubic-bezier(0.4, 0, 0.2, 1) !important", // Smooth, controlled transition for checked state
          "&.Mui-checked": {
            transform: "translateX(12px) !important", // Adjusted for smaller size (24px in CustomSwitch, reduced proportionally)
          },
        },
        thumb: {
          width: "10 !important", // Reduced from 22px (matches your CustomSwitch, but smaller)
          height: "10 !important", // Reduced from 22px (matches your CustomSwitch, but smaller)
          willChange: "transform", // Optimize for smooth transitions
          transition: "transform 150ms cubic-bezier(0.4, 0, 0.2, 1) !important", // Smooth thumb transition
        },
        track: {
          borderRadius: "8 !important", // Slightly smaller radius (from 13px in CustomSwitch, reduced proportionally)
          willChange: "background-color", // Optimize for smooth background changes
          transition: "background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) !important", // Smooth track color transition
        },
      },
      defaultProps: {
        size: "small", // Default to smaller switches globally
      },
    },

    MuiIcon: {
      styleOverrides: {
        root: {
          fontSize: "0.625rem !important", // 7.5px at htmlFontSize: 12, preserved
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiInputBase-root": {
            fontSize: "0.875rem", // Reduced text size to 10.5px
            height: "36px", // Reduced height
            padding: "4px 8px", // Reduced padding
          },
          "& .MuiInputLabel-root": {
            fontSize: "0.875rem", // Reduced label text size
            transform: "translate(14px, 12px) scale(1)", // Adjusted for smaller size
          },
          "& .MuiInputLabel-shrink": {
            transform: "translate(14px, -6px) scale(0.75)", // Adjusted for smaller size
          },
          "& .MuiInputAdornment-root": {
            marginRight: "4px", // Reduced margin for icons
          },
        },
      },
      defaultProps: {
        size: "small", // Default to smaller text fields globally
      },
    },
  },
});

export default theme;